<template>
  <div class="z-card__body mt-3">
    <template v-if="!is_view_loading">
      <div v-if="views.email_send" class="z-card__body mt-3">
        {{
          $t('core.reset_password.send_email_to_confirm', {
            email: $user.email
          })
        }}

        <div class="row mt-4">
          <div class="col-12 text-right">
            <i-button
              type="primary"
              :loading="is_view_loading"
              :disabled="is_view_loading"
              @click="send_email"
            >
              {{ $t('core.reset_password.send') }}
            </i-button>
          </div>
        </div>
      </div>

      <div v-else class="z-card__body mt-3">
        <p v-if="views.user_ban">
          {{ $t('core.reset_password.day_block') }}
        </p>
        <p v-if="views.success_send_email">
          {{
            $t('core.reset_password.success_send_code', {
              email: $user.email
            })
          }}
        </p>

        <div>
          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('core.reset_password.field.code') }}
            </div>
            <z-form-input class="col-9" :rules="{ min: 6, max: 6 }" role="presentation">
              <i-input v-model="code" type="text" autocomplete="false" :disabled="is_code_verified">
                <template #suffix>
                  <icon-suffix :is_code_verified="is_code_verified" />
                  <icon-suffix
                    :is_running="is_timer_running"
                    :is_code_verified="is_code_verified"
                    :pretty_time="convertMinutesToHours"
                  />
                </template>
              </i-input>
              <popup
                v-if="!is_code_verified && !views.user_ban"
                width="370px"
                top="-20px"
                left="inherit"
                right="-370px"
              >
                <p v-if="views.code_exist" class="text-left" style="text-indent: -4px">
                  {{ $t('core.reset_password.code_exist') }}
                </p>
                <div
                  style="text-indent: -4px"
                  :class="{
                    link: true,
                    'link--disabled': views.disable_resending
                  }"
                  @click="handle_send_code"
                >
                  {{ $t('core.reset_password.link.send_repeated') }}
                </div>
              </popup>
            </z-form-input>
          </z-form-group>
        </div>

        <div :class="{ 'disabled-block': !is_code_verified }">
          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('core.profile.form.field.password.new') }}
            </div>
            <z-form-input
              class="col-9"
              :rules="{
                required: true,
                min: 6,
                max: 128,
                password: true
              }"
            >
              <i-input
                v-model="form.new_password"
                :type="new_password_params.type"
                autocomplete="new-password"
              >
                <template #suffix>
                  <div
                    :class="{
                      row: !!form.new_password && new_password_params.icon === 'md-eye-off'
                    }"
                  >
                    <span
                      v-if="!!form.new_password && new_password_params.icon === 'md-eye-off'"
                      class="flex copy-row mr-1"
                      @click="copy"
                    >
                      <icon type="md-copy" size="20" style="margin-left: -4px" />
                    </span>

                    <icon
                      v-if="!!form.new_password"
                      :type="new_password_params.icon"
                      size="20"
                      @click="toggle_new_password"
                    />
                  </div>
                </template>
              </i-input>

              <popup
                v-if="is_code_verified"
                width="200px"
                style="top: -75px; right: -200px; left: inherit"
              >
                <p>{{ $t('core.reset_password.password_requirements') }}</p>
                <conditions :valid_result="valid_result" />
                <div
                  :class="{ link: true, 'mt-2': true, 'link--disabled': is_generation_loading }"
                  @click="generate_password"
                >
                  {{ $t('core.reset_password.generate_password') }}
                </div>
              </popup>
            </z-form-input>
          </z-form-group>

          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('core.profile.form.field.password.new_confirm') }}
            </div>
            <z-form-input class="col-9" :rules="{ required: true, min: 6, max: 128 }">
              <i-input
                v-model="form.new_password_confirmation"
                :type="confirm_password_params.type"
                autocomplete="nope"
              >
                <template #suffix>
                  <icon
                    v-if="!!form.new_password_confirmation"
                    :type="confirm_password_params.icon"
                    size="20"
                    @click="toggle_confirm_password"
                  />
                </template>
              </i-input>
            </z-form-input>
          </z-form-group>
        </div>

        <div class="row">
          <div class="col-12 text-right">
            <i-button
              type="default"
              size="large"
              :loading="is_loading"
              :disabled="!password_is_valid || is_loading"
              @click="submit"
            >
              {{ $t('shared.component.button.change') }}
            </i-button>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="card__body flex">
      <spin
        size="large"
        style="
          display: flex;
          align-items: center;
          text-align: center;
          margin: 0 auto;
          height: 200px;
        "
      />
    </div>
  </div>
</template>

<script>
  import { ResetPasswordService } from '@core/services/ResetPasswordService';
  import { EmailVerificationService } from '@core/services/EmailVerificationService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import Conditions from '@shared/components/reset-password-form/conditions';
  import { validations } from '@utils/password-rules';
  import IconSuffix from '@core/components/auth/reset-password/icon-suffix';
  import Timer from '@core/components/auth/reset-password/timer';
  import moment from 'moment-es6';
  import Vue from 'vue';

  const initialViews = {
    email_send: false,
    success_send_email: false,
    user_ban: false,
    disable_resending: false,
    code_exist: false
  };
  export default {
    components: { Conditions, IconSuffix },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      form: {
        new_password: '',
        new_password_confirmation: ''
      },
      current_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      new_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      confirm_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      code: '',
      is_loading: false,
      is_view_loading: false,
      is_generation_loading: false,
      valid_result: [null, null, null, null, null],
      views: initialViews,
      is_code_verified: false,
      is_timer_running: false,
      timer: null,
      can_send_at_timestamp: 0
    }),

    computed: {
      password_is_valid: function () {
        return Object.entries(this.valid_result).every(([key, results]) => !!results);
      },

      convertMinutesToHours() {
        let timestamp = this.can_send_at_timestamp;
        let hours = Math.floor(timestamp / 3600).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });
        let minutes = Math.floor((timestamp / 60) % 60).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });
        let seconds = (timestamp % 60).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });

        return `${hours}:${minutes}:${seconds}`;
      }
    },
    watch: {
      code: {
        handler: function (value) {
          if (value.replace(/ /g, '').length === 6 && !this.is_code_verified) {
            this.verify_code();
          }
        },
        immediate: true,
        deep: true
      },
      'form.current_password': {
        handler: function () {
          if (this.form.current_password == '') {
            this.current_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }
        },
        immediate: true,
        deep: true
      },
      'form.new_password': {
        handler: function (value) {
          if (this.form.new_password == '') {
            this.new_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(value, this.form.new_password_confirmation)
                : fn(value, this.form.new_password_confirmation) || null;
            } else {
              return this.valid_result[i] === false ? fn(value) : fn(value) || null;
            }
          });
        },
        immediate: true,
        deep: true
      },
      'form.new_password_confirmation': {
        handler: function (value) {
          if (this.form.new_password_confirmation == '') {
            this.confirm_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(this.form.new_password, value)
                : fn(this.form.new_password, value) || null;
            } else {
              return this.valid_result[i] === false
                ? fn(this.form.new_password)
                : fn(this.form.new_password) || null;
            }
          });
        },
        immediate: true,
        deep: true
      }
    },
    created() {
      this.check_last_sent();
    },
    methods: {
      copy() {
        this.$copyText(this.form.new_password).then(() => {
          this.$noty.success(this.$t('shared.component.noty.text_copied'));
        });
      },

      handle_send_code() {
        if (!this.views.disable_resending) {
          this.check_last_sent();
        }
      },

      check_last_sent() {
        this.views.email_send = false;
        this.views.success_send_email = false;
        this.views.user_ban = false;
        this.views.disable_resending = false;
        this.views.code_exist = false;

        this.is_view_loading = true;
        ResetPasswordService.checkLastSent({ email: this.$user.email })
          .then(({ has_new_status, can_send_at, is_banned }) => {
            if (is_banned) {
              return (this.views.user_ban = true);
            }

            if (has_new_status) {
              const now_date = Math.round(new Date().getTime() / 1000);
              const can_send_at_date = can_send_at;
              if (now_date > can_send_at_date) {
                this.send_email();
              } else {
                this.views.disable_resending = true;
                this.can_send_at_timestamp = can_send_at_date - now_date;
                this.start();
              }

              return (this.views.code_exist = true);
            } else if (!has_new_status) {
              return (this.views.email_send = true);
            }
          })
          .finally(() => (this.is_view_loading = false));
      },

      send_email() {
        this.is_view_loading = true;

        this.views.email_send = false;
        this.views.user_ban = false;
        this.views.success_send_email = false;
        this.views.disable_resending = false;
        this.views.code_exist = false;

        ResetPasswordService.send({ email: this.$user.email })
          .then(() => {
            this.views.success_send_email = true;
          })
          .catch((error) => {
            if (
              error?.response?.data?.errors?.email.filter(
                (i) => i.message === 'wait_attempts_time_interval'
              ).length > 0
            ) {
              this.views.user_ban = true;
            } else {
              this.$noty.server_error(error);
            }
          })
          .finally(() => (this.is_view_loading = false));
      },

      toggle_current_password() {
        this.current_password_params = {
          type: this.current_password_params.type === 'password' ? 'text' : 'password',
          icon: this.current_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      toggle_new_password() {
        this.new_password_params = {
          type: this.new_password_params.type === 'password' ? 'text' : 'password',
          icon: this.new_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      toggle_confirm_password() {
        this.confirm_password_params = {
          type: this.confirm_password_params.type === 'password' ? 'text' : 'password',
          icon: this.confirm_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      generate_password: function () {
        this.is_generation_loading = true;
        ResetPasswordService.generate()
          .then(({ password }) => {
            this.form.new_password = password || '';
            this.new_password_params = {
              type: (this.new_password_params.type = 'text'),
              icon: (this.new_password_params.icon = 'md-eye-off')
            };
          })
          .finally(() => (this.is_generation_loading = false));
      },

      submit: function () {
        if (this.password_is_valid) {
          this.is_loading = true;
          ResetPasswordService.changePassword({
            email: this.$user.email,
            email_code: this.code,
            new_password: this.form.new_password,
            new_password_confirmation: this.form.new_password_confirmation
          })
            .then(() => {
              setTimeout(() => {
                this.$router.push({ name: 'auth.login' });
              }, 2000);
              this.$noty.success(
                this.$t('core.reset_password.success_change_password') +
                  ' ' +
                  this.$t('core.reset_password.link.success_change_link_to_auth_page')
              );
              this.is_loading = false;
            })
            .catch((event) => {
              const R = Vue.$R;

              const new_password_error = R.pathOr(
                '',
                ['response', 'data', 'errors', 'new_password', 0, 'message'],
                event
              );

              if (new_password_error === 'The new password format is invalid.') {
                this.$noty.error(this.$t('core.reset_password.error.invalid_format'));
              } else {
                this.$noty.server_error(event);
              }
            })
            .finally(() => (this.is_loading = false));
        } else if (this.form.new_password.length <= 0 || this.form.new_password_confirmation <= 0) {
          this.$noty.error(this.$t('shared.errors.input_form'));
        } else {
          this.$noty.error(this.$t('core.reset_password.error.not_valid_password'));
        }
      },

      verify_code() {
        if (this.code.replace(/ /g, '').length === 6) {
          EmailVerificationService.check({ email: this.$user.email, code: this.code })
            .then((response) => {
              if (response?.success) {
                this.is_code_verified = true;
              } else {
                let server_error = this.$t(`core.reset_password.error.${response.errors}`);
                this.$noty.error(server_error);
              }
            })
            .catch((event) => {
              this.is_code_verified = false;

              const R = Vue.$R;
              const error_key = R.pathOr('', ['response', 'data', 'errors'], event);

              if (error_key === 'code_size') {
                this.$noty.error(this.$t('core.reset_password.error.code_size'));
              } else {
                this.$noty.server_error(event);
              }
            });
        }
      },

      start() {
        this.is_timer_running = true;
        if (!this.timer) {
          this.timer = setInterval(() => {
            if (this.can_send_at_timestamp > 0) {
              this.can_send_at_timestamp = this.can_send_at_timestamp - 1;
            } else {
              clearInterval(this.timer);
              this.reset();
            }
          }, 1000);
        }
      },
      stop() {
        this.is_timer_running = false;
        clearInterval(this.timer);
        this.timer = null;
      },
      reset() {
        this.stop();
        this.views.disable_resending = false;
        this.can_send_at_timestamp = 0;
        this.server_error = '';
      }
    }
  };
</script>
